"use client";

import { useEffect, useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/auth/password-input";
import { createClient } from "@/lib/supabase/client";
import { useAuthModal } from "@/components/auth/auth-modal-context";
import { translateAuthError } from "@/lib/auth-errors";

type Mode = "login" | "signup" | "reset";
type Status = "idle" | "loading" | "signup-success" | "reset-sent" | "error";

export function AuthModal() {
  const { isOpen, initialMode, reason, closeAuthModal } = useAuthModal();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Nur fuer Registrierung genutzt (Feedback-Runde 10 Punkt 2, Tippfehler-Schutz beim
  // Passwort-Setzen) - beim Login reicht ein Feld, da das Passwort dort bereits feststeht.
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  // Bei jedem Oeffnen den vom Aufrufer gewuenschten Modus uebernehmen (Erik 2026-07-17:
  // Konto-Gate soll direkt im Signup-Formular landen, nicht erst im Login). Nur beim
  // UEBERGANG zu offen synchronisieren, nicht bei jedem Re-Render, sonst wuerde ein
  // manueller switchMode()-Klick des Nutzers waehrend die Modal offen ist wieder
  // zurueckgesetzt. setState nicht synchron im Effect-Body (react-hooks/set-state-in-effect,
  // gleiches Muster wie anderswo im Projekt) - ueber einen 0ms-Timer statt direkt.
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => setMode(initialMode), 0);
    return () => clearTimeout(id);
  }, [isOpen, initialMode]);

  function resetForm() {
    setMode("login");
    setStatus("idle");
    setError(null);
    setEmail("");
    setPassword("");
    setPasswordConfirm("");
  }

  function switchMode(next: Mode) {
    setMode(next);
    setStatus("idle");
    setError(null);
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      closeAuthModal();
      resetForm();
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setError(null);

    const supabase = createClient();

    if (mode === "reset") {
      const { error: authError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (authError) {
        setStatus("error");
        setError(translateAuthError(authError.message));
        return;
      }

      setStatus("reset-sent");
      return;
    }

    if (mode === "signup" && password !== passwordConfirm) {
      setStatus("error");
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    const { error: authError } =
      mode === "login"
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({
            email,
            password,
            options: { emailRedirectTo: window.location.origin },
          });

    if (authError) {
      setStatus("error");
      setError(translateAuthError(authError.message));
      return;
    }

    if (mode === "login") {
      closeAuthModal();
      resetForm();
    } else {
      setStatus("signup-success");
    }
  }

  const isLogin = mode === "login";
  const isReset = mode === "reset";

  const title = isReset ? "Passwort zurücksetzen" : isLogin ? "Anmelden" : "Konto erstellen";
  const description = isReset
    ? "Gib deine E-Mail-Adresse ein, wir schicken dir einen Link zum Zurücksetzen."
    : isLogin
      ? "Melde dich an, um deine Watchlist und Depots zu nutzen."
      : (reason ?? "Erstelle ein kostenloses Konto, um Aktien zu deiner Watchlist hinzuzufügen.");

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {status === "signup-success" ? (
          <p className="text-sm text-ink-secondary">
            Fast geschafft! Bitte bestätige deine E-Mail-Adresse über den Link, den wir
            dir gerade geschickt haben.
          </p>
        ) : status === "reset-sent" ? (
          <p className="text-sm text-ink-secondary">
            Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir dir einen Link zum
            Zurücksetzen deines Passworts geschickt.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="deine@email.de"
              aria-label="E-Mail-Adresse"
              className="bg-card"
            />
            {!isReset && (
              <PasswordInput
                value={password}
                onChange={setPassword}
                placeholder="Passwort"
                ariaLabel="Passwort"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
              />
            )}
            {mode === "signup" && (
              <PasswordInput
                value={passwordConfirm}
                onChange={setPasswordConfirm}
                placeholder="Passwort bestätigen"
                ariaLabel="Passwort bestätigen"
                autoComplete="new-password"
              />
            )}
            {isLogin && (
              <button
                type="button"
                onClick={() => switchMode("reset")}
                className="self-end text-xs text-muted-foreground underline underline-offset-3 hover:text-foreground"
              >
                Passwort vergessen?
              </button>
            )}
            <Button type="submit" disabled={status === "loading"}>
              {status === "loading"
                ? "Einen Moment…"
                : isReset
                  ? "Link senden"
                  : isLogin
                    ? "Anmelden"
                    : "Konto erstellen"}
            </Button>
            {status === "error" && error && <p className="text-xs text-negative">{error}</p>}
          </form>
        )}

        <p className="text-center text-xs text-muted-foreground">
          {isReset ? (
            <button
              type="button"
              onClick={() => switchMode("login")}
              className="underline underline-offset-3 hover:text-foreground"
            >
              Zurück zum Login
            </button>
          ) : isLogin ? (
            <>
              Noch kein Konto?{" "}
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className="underline underline-offset-3 hover:text-foreground"
              >
                Jetzt registrieren
              </button>
            </>
          ) : (
            <>
              Schon ein Konto?{" "}
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="underline underline-offset-3 hover:text-foreground"
              >
                Anmelden
              </button>
            </>
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}
